import type { InputDisposition } from "../../Store/Reducers/AssessmentSlice";
import { formatAssessmentDateUtc } from "../../Utils/Helper/assessmentDateUtils";

export type AssessmentSubmitSuccessKind =
  | "team"
  | "self"
  | "formal"
  | "disconnect"
  /** ESL hub: publishPostResolution after Force Publish on a disconnect row. */
  | "disconnect_force_publish"
  /** ESL hub: publishAssessmentAccounts(force) from the Ongoing tab. */
  | "assessment_force_publish"
  /** ESL hub: override final level on a published account (Published tab). */
  | "published_level_override"
  /** ESL hub: override + hub publish from Ongoing / Disconnect Ongoing. */
  | "level_override_publish";

export interface AssessmentSubmitSuccessInfo {
  /** Product: `self` is Team. Prefer `team` in new code. */
  kind: AssessmentSubmitSuccessKind;
  accountId: string;
  accountName: string;
  levelLabel?: string | null;
  dateLabel?: string | null;
  variant?: "initiated" | "joined";
  /** Product: Team disconnect when true. */
  isSelfDisconnect?: boolean;
  dueDateLabel?: string | null;
  /** Private remainder input: next Team cycle publish if no sooner round. */
  remainderCountsOnLabel?: string | null;
  inputDisposition?: InputDisposition | null;
  /** Included Formal input: last day to reassess (day after the round due date). */
  reassessBeforeLabel?: string | null;
  /** Hub force publish: partial account selection count. */
  publishedAccountCount?: number;
  assessmentTitle?: string | null;
}

export type AssessmentSubmitSuccessCalloutTone = "team" | "esl";

export type AssessmentSubmitSuccessCopy = {
  title: string;
  body: string;
  callout: { tone: AssessmentSubmitSuccessCalloutTone; text: string };
  showMixCta: boolean;
};

const LEVEL_FALLBACK = "your selected level";

/** My Assessments status hover for a private remainder input. */
export const getPrivateRemainderPendingHint = (
  countsOnPublishLabel: string
): string =>
  `Your input is private for now. When the next Team window opens or a Formal starts on this account, your current input is included in that round automatically; you can update it before that round publishes. If nothing runs sooner, it counts when the next Team cycle publishes on ${countsOnPublishLabel}, if it is still current then.`;

/** My Assessments status hover when a disconnect holds the member's input. */
export const getHeldDisconnectInputHint = (): string =>
  "A disconnect is open on this account. Your level stays private and does not count toward the published acceptance level until the disconnect is finished. After it publishes, your input is added to the running Team or Formal when possible, or it counts in the next assessment cycle.";

const privateRemainderFollowUpLine = (countsOnPublishLabel: string): string =>
  `When the next Team window opens or a Formal starts, your input is included in that round automatically. If nothing runs sooner, it counts on ${countsOnPublishLabel} if it is still current then.`;

const privateRemainderNoWindowCallout = (
  countsOnPublishLabel: string
): string =>
  `No assessment window is open for this account right now. Your input stays private. When the next Team window or Formal starts, it is included in that round automatically. Otherwise it counts on ${countsOnPublishLabel} if it is still current then.`;

export const formatAssessmentSubmitSuccessDate = (
  value?: string | null
): string | null => {
  if (!value) return null;
  const label = formatAssessmentDateUtc(value, "US_LONG");
  return label || null;
};

export const formatAssessmentSubmitSavedOnDate = (
  value?: string | null
): string =>
  formatAssessmentSubmitSuccessDate(value || new Date().toISOString()) ||
  "today";

export const formatReassessBeforeLabel = (
  roundDueDate?: string | null
): string | null => {
  if (!roundDueDate) return null;
  const due = new Date(`${roundDueDate.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) return null;
  due.setUTCDate(due.getUTCDate() + 1);
  return formatAssessmentSubmitSuccessDate(due.toISOString());
};

const dispositionCallout = (
  disposition: InputDisposition,
  accountName: string,
  info: AssessmentSubmitSuccessInfo
): string | null => {
  switch (disposition) {
    case "private_no_round": {
      const base =
        "Since there is no assessment running at this time, your responses are stored privately and will be counted automatically in the next assessment cycle.";
      return info.remainderCountsOnLabel
        ? `${base}\n${privateRemainderFollowUpLine(info.remainderCountsOnLabel)}`
        : base;
    }
    case "held_next_cycle":
      return "Since a disconnect is running on this account, your responses are stored privately and will be counted automatically in the next assessment cycle.";
    case "held_until_release_formal":
      return "Since a disconnect is running on this account, your responses are stored privately and will be added automatically to the running assessment as soon as the disconnect is published.";
    case "counted_both":
      return `Your input counts in both the Team assessment and the running Formal on ${accountName}.`;
    case "updated_both":
      return `Your input has been updated in both the running Team assessment and the running Formal on ${accountName}.`;
    case "held_until_release_team":
      return `A disconnect is running on ${accountName}. Your input is stored privately and will be added to the Team assessment after the disconnect is published, if the window is still open. Otherwise it counts in the next cycle.`;
    case "included_formal":
      return info.reassessBeforeLabel
        ? `Your input is included in the running Formal on ${accountName}. You can reassess any time before ${info.reassessBeforeLabel}.`
        : `Your input is included in the running Formal on ${accountName}.`;
    case "formal_closed_private":
      return `The Formal on ${accountName} is closed for inputs. Your responses are stored privately and will count in the next assessment cycle.`;
    case "held_misses_formal":
      return `A disconnect is running on ${accountName}, and the Formal publishes before it closes. Your responses are stored privately and will count in the next assessment cycle.`;
    default:
      return null;
  }
};

export const isTeamSubmitSuccessKind = (
  kind: AssessmentSubmitSuccessKind
): boolean => kind === "team" || kind === "self";

export const getAssessmentSubmitSuccessCopy = (
  info: AssessmentSubmitSuccessInfo
): AssessmentSubmitSuccessCopy => {
  const accountName = info.accountName || "the account";
  const levelLabel = info.levelLabel || LEVEL_FALLBACK;
  const savedOnDate = info.dateLabel || "today";
  const dueDate = info.dueDateLabel?.trim() || null;
  const savedBody = `Your assessment for ${accountName} as ${levelLabel} has been saved on ${savedOnDate}`;
  const disconnectBody = `Your disconnect resolution for ${accountName} has been submitted successfully.`;

  const dispositionText =
    info.kind !== "disconnect" && info.inputDisposition
      ? dispositionCallout(info.inputDisposition, accountName, info)
      : null;
  if (dispositionText) {
    return {
      title: "Assessment saved",
      body: savedBody,
      callout: { tone: "team", text: dispositionText },
      showMixCta:
        info.inputDisposition === "counted_both" ||
        info.inputDisposition === "updated_both" ||
        info.inputDisposition === "included_formal",
    };
  }

  if (info.remainderCountsOnLabel) {
    return {
      title: "Assessment saved",
      body: savedBody,
      callout: {
        tone: "team",
        text: privateRemainderNoWindowCallout(info.remainderCountsOnLabel),
      },
      showMixCta: false,
    };
  }

  if (info.kind === "disconnect_force_publish") {
    const levelLabel = info.levelLabel || LEVEL_FALLBACK;
    const publishedOn = info.dateLabel || "today";
    return {
      title: "Disconnect published",
      body: `The disconnect for ${accountName} has been published at ${levelLabel} on ${publishedOn}.`,
      callout: {
        tone: "esl",
        text: "The published level is the Primary Sponsor's recorded resolution. Participants were notified.",
      },
      showMixCta: false,
    };
  }

  if (info.kind === "published_level_override") {
    const level = info.levelLabel || LEVEL_FALLBACK;
    const savedOn = info.dateLabel || "today";
    return {
      title: "Level overridden",
      body: `The acceptance level for ${accountName} is now ${level} as of ${savedOn}.`,
      callout: {
        tone: "esl",
        text: "Individual sponsor responses are unchanged. Open View full report to review rationale and inputs.",
      },
      showMixCta: false,
    };
  }

  if (info.kind === "level_override_publish") {
    const level = info.levelLabel || LEVEL_FALLBACK;
    const savedOn = info.dateLabel || "today";
    return {
      title: "Level overridden and published",
      body: `The acceptance level for ${accountName} is now ${level} and the account was published on ${savedOn}.`,
      callout: {
        tone: "esl",
        text: "If a disconnect was open on this account, it was cleared as part of publish.",
      },
      showMixCta: false,
    };
  }

  if (info.kind === "assessment_force_publish") {
    const assessmentTitle = info.assessmentTitle?.trim() || "this assessment";
    const count = info.publishedAccountCount;
    const publishedOn = info.dateLabel || "today";
    const body =
      count != null && count > 0
        ? `${count} account${count === 1 ? "" : "s"} from ${assessmentTitle} ${count === 1 ? "has" : "have"} been force-published on ${publishedOn}.`
        : `${assessmentTitle} has been force-published on ${publishedOn}.`;
    const partialFollowUp =
      count != null && count > 0
        ? "Unselected accounts stay on Ongoing and auto-publish on due+1."
        : null;

    return {
      title: "Assessment published",
      body,
      callout: {
        tone: "esl",
        text: partialFollowUp
          ? `Aligned accounts are published; disconnect accounts enter the disconnect flow where applicable.\n${partialFollowUp}`
          : "Aligned accounts are published; disconnect accounts enter the disconnect flow where applicable.",
      },
      showMixCta: false,
    };
  }

  if (info.kind === "disconnect") {
    const isTeamDisconnect = Boolean(info.isSelfDisconnect);
    const helper =
      "To complete another pending formal assessment, open Assessment Status.";
    const dueLine =
      !isTeamDisconnect && dueDate
        ? `You can update this resolution until ${dueDate}.`
        : null;
    const pendingLine =
      "This stays pending until the disconnect due date, then it publishes automatically.";

    return {
      title: "Disconnect resolution submitted",
      body: disconnectBody,
      callout: isTeamDisconnect
        ? { tone: "team", text: `${pendingLine}\n${helper}` }
        : {
            tone: "team",
            text: dueLine ? `${dueLine}\n${helper}` : helper,
          },
      showMixCta: false,
    };
  }

  if (isTeamSubmitSuccessKind(info.kind)) {
    const inputsLine = dueDate
      ? `The account team and sponsors have been notified to provide their inputs by ${dueDate}.`
      : "The account team and sponsors have been notified to provide their inputs.";
    return {
      title: "Assessment submitted",
      body: savedBody,
      callout: {
        tone: "team",
        text: `${inputsLine}\nThe final assessment will be published after that.`,
      },
      showMixCta: true,
    };
  }

  const formalHelper =
    "To complete another pending formal or team assessment, open Assessment Status.";
  const formalDue = dueDate
    ? `You can update your responses until ${dueDate}.`
    : null;

  return {
    title: "Assessment submitted",
    body: savedBody,
    callout: {
      tone: "team",
      text: formalDue ? `${formalDue}\n${formalHelper}` : formalHelper,
    },
    showMixCta: true,
  };
};
